import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ActiveBet } from '../../features/active-bet/active-bet';
import { GameStatusEnum } from '../../game-status/constants/game-status-enum';
import { useGameStatusStore } from '../../game-status/store/game-status-store';
import clsx from 'clsx';

import { Score } from 'modules/Room/features/Score/Score';
import { ChatAvatar } from 'modules/Room/features/chat-avatar/chat-avatar';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import {
  DEFAULT_SLEEP_TIMEOUT,
  DEFAULT_Z_ICON_COUNT,
  NULL_ADDRESS,
} from 'shared/constants/constants';
import { RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { useRoomStore } from 'shared/store/roomStore';
import { prettyTx } from 'shared/utils/pretty-tx';
import { getScannerLink } from 'shared/utils/scanner-link';

import ZIcon from 'assets/animation/sleep/Z.svg?react';

import { Hearts } from './hearts';
import styles from './mobile-game-card.module.css';

interface IMobileGameCardProps {
  className?: string;
}

const MobileGameCard = ({ className }: IMobileGameCardProps) => {
  const { t } = useTranslation();
  const { address, chainId } = useAccount();
  const { activeRoomId } = useParams();
  const { gameStatus } = useGameStatusStore();
  const {
    amIPlayerA,
    playerA,
    playerB,
    someoneElseGame,
    winnerStatus,
    roomStatus,
    winsTo,
    roundCounter,
  } = useRoomStore();
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const [showSleep, setShowSleep] = useState(false);
  const [amIWinner, setAmIWinner] = useState(false);

  const sleepTimeoutRef = useRef<NodeJS.Timeout>();

  const isRoomClosed = useMemo(() => roomStatus === RoomStatusEnum.Closed, [roomStatus]);

  const playerAWinner = useMemo(() => {
    if (!isRoomClosed) return null;
    return [WinnerStatusEnum.WinnerPlayerA, WinnerStatusEnum.TechnicalWinnerA].includes(
      winnerStatus,
    );
  }, [isRoomClosed, winnerStatus]);

  const getPlayerLabel = (playerAddress: `0x${string}`) => {
    let prettyPlayerAddress = prettyTx({
      address: playerAddress,
      leaderboardMap,
      startAddressLength: 4,
      endAddressLength: 3,
      discordUsernameLength: 10,
    });

    if (playerAddress === address?.toLowerCase()) return prettyPlayerAddress;
    if (!activeRoomId && playerAddress === NULL_ADDRESS) return 'common.noOpponent';
    if (playerAddress === NULL_ADDRESS) return 'common.connecting';
    return prettyPlayerAddress;
  };

  const renderPlayerCard = useCallback(
    (player: typeof playerA, isPlayerA: boolean, playerWins: number, orderClass: string) => {
      const isCurrentUser = player.address.toLowerCase() === address?.toLowerCase();
      const isWinner =
        (isPlayerA && playerAWinner) || (!isPlayerA && isRoomClosed && !playerAWinner);

      return (
        <div className={`${orderClass} flex flex-col items-center`}>
          <div className="background-dark-background relative flex flex-col items-center">
            {activeRoomId && player.address !== NULL_ADDRESS ? (
              <ChatAvatar
                userAddress={player.address}
                isGameInProgress={
                  playerA.address !== NULL_ADDRESS && playerB.address !== NULL_ADDRESS
                }
                amIHost={player.address.toLowerCase() === address?.toLowerCase()}
                className="h-11 w-14 rounded-b-none rounded-t-lg border border-b-0 border-[#202066] sm:h-[110px] sm:w-[110px]"
              />
            ) : (
              <p className="flex h-11 w-14 items-center justify-center rounded-t-lg border border-b-0 border-[#202066] bg-[#0C093D] bg-opacity-90 text-center text-[44px] text-blue sm:h-[110px] sm:w-[110px] sm:text-[76px]">
                ?
              </p>
            )}

            {player.address !== NULL_ADDRESS ? (
              <a
                href={getScannerLink(player.address, chainId?.toString())}
                target="_blank"
                className={clsx(
                  isCurrentUser ? styles.playerName : styles.playerOpponent,
                  'font-chivo',
                )}
              >
                {t(getPlayerLabel(player.address))}
              </a>
            ) : (
              <span
                className={clsx(
                  isCurrentUser ? styles.playerName : styles.playerOpponent,
                  'font-chivo',
                )}
              >
                {t(getPlayerLabel(player.address))}
              </span>
            )}

            <AnimatedTransition
              show={
                showSleep &&
                ((amIPlayerA && !isPlayerA) || (!amIPlayerA && isPlayerA)) &&
                roomStatus !== RoomStatusEnum.Closed
              }
            >
              {Array.from({ length: DEFAULT_Z_ICON_COUNT }).map((_, index) => (
                <ZIcon key={index} className={styles.zIconMobile} />
              ))}
            </AnimatedTransition>
          </div>

          <div
            className={clsx(
              'mt-1 flex w-full gap-3 sm:gap-4',
              orderClass === 'order-3' ? 'scale-x-[-1] transform' : '',
            )}
          >
            <Hearts
              wins={playerWins}
              winsTo={Number(winsTo)}
              prefix={isPlayerA ? 'a' : 'b'}
              opponent={orderClass === 'order-3'}
            />
          </div>
        </div>
      );
    },
    [
      address,
      someoneElseGame,
      amIPlayerA,
      isRoomClosed,
      playerA.address,
      playerB.address,
      playerAWinner,
      chainId,
      winsTo,
      showSleep,
    ],
  );

  const classNamePlayerACard = useMemo(
    () => (someoneElseGame ? 'order-1' : amIPlayerA ? 'order-1' : 'order-3'),
    [someoneElseGame, amIPlayerA],
  );

  const classNamePlayerBCard = useMemo(
    () => (someoneElseGame ? 'order-3' : amIPlayerA ? 'order-3' : 'order-1'),
    [someoneElseGame, amIPlayerA],
  );

  useEffect(() => {
    if (
      gameStatus === GameStatusEnum.WAIT_OPPONENT_MOVE ||
      gameStatus === GameStatusEnum.WAIT_OPPONENT_REVEAL
    ) {
      sleepTimeoutRef.current = setTimeout(() => setShowSleep(true), DEFAULT_SLEEP_TIMEOUT);
    } else {
      clearTimeout(sleepTimeoutRef.current!);
      setShowSleep(false);
    }
  }, [gameStatus]);

  useEffect(() => {
    if (!isRoomClosed || playerB.address === NULL_ADDRESS) return;

    const didIWin =
      (amIPlayerA &&
        [WinnerStatusEnum.WinnerPlayerA, WinnerStatusEnum.TechnicalWinnerA].includes(
          winnerStatus,
        )) ||
      (!amIPlayerA &&
        [WinnerStatusEnum.WinnerPlayerB, WinnerStatusEnum.TechnicalWinnerB].includes(
          winnerStatus,
        )) ||
      (amIPlayerA && winnerStatus === WinnerStatusEnum.PlayerAExitRoom);

    setAmIWinner(didIWin);
  }, [isRoomClosed, winnerStatus, amIPlayerA, playerB.address]);

  return (
    <div className={clsx(styles.container, className)}>
      {renderPlayerCard(playerA, true, Number(playerB.wins), classNamePlayerACard)}

      <div className="order-2 flex w-full flex-col gap-1 sm:gap-2">
        <Score />
        <ActiveBet amIWinner={amIWinner} isGameFinished={isRoomClosed} />
      </div>

      {renderPlayerCard(playerB, false, Number(playerA.wins), classNamePlayerBCard)}
    </div>
  );
};

export default MobileGameCard;
